import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from "react-router-dom";
import ProjectBackgroundImage from "../../assets/EMB.png";
import mgaConstants from "../../constants/index";
import HelpSection from "../help";
import { NavLink } from "react-router-dom";
import env from "../../env";
import MGAAPI from "../../api/index";
import dayjs from "dayjs";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Menu,
  Paper,
  Popover,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  UnstyledButton,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useAtom } from "jotai";
import { sideMenu } from "../../utils/atoms/sideMenuAtom";
import { scopeListAccessAtom } from "../../utils/atoms/scopeListAccessAtom";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import {
  IconArrowRight,
  IconCalendarDue,
  IconCaretDownFilled,
  IconInfoCircle,
  IconMoonStars,
  IconSunHigh,
} from "@tabler/icons-react";
import { SeIcon } from "@se/web-ui-react";
import { dialogSimulationAtom } from "../../utils/atoms/dialogSimulation";
import SimulationDialog from "../simulation/simulaitonDialoge";
import { collapseAtom } from "../../utils/atoms/collapseAtom";
import { activeTabAtom } from "../../utils/atoms/activeTabAtom";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { useTour } from "../../utils/tour";
import { tourAtom } from "../../utils/atoms/tourAtom";
import LegalDocument from "../../pages/TaC/LegalDocument";
import ProfileModal from "./ProfileModal";
import { setProjectFormats } from "../../utils/helpers";

const apiSession = new MGAAPI();
const src = "url(" + ProjectBackgroundImage + ")";
const privacyPolicy = `https://www.se.com/ww/en/about-us/legal/data-privacy.jsp`;

const MGAShellLayout = (props) => {
  const { i18n, t } = useTranslation();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // i18n for language switching
  const [sideMenuShow, setSideMenu] = useAtom(sideMenu);
  const [snackBar, setSnackBar] = useState({ type: "", message: "" });
  const projectNavigationRef = useRef(null);
  const legalNavigationRef = useRef(null);
  const [dialogSimulation] = useAtom(dialogSimulationAtom);
  const [, setCollapseSideMenu] = useAtom(collapseAtom);
  const [activeState] = useAtom(activeTabAtom);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const [, setProjectFormatsVersion] = useState(0);

  const projectName = projectDetails?.projectDTO?.projectName;
  const projectId = projectDetails?.projectDTO?.projectId;

  useEffect(() => {
    setProjectFormats({
      dateFormat: projectDetails?.projectDTO?.dateFormat,
      numberFormat: projectDetails?.projectDTO?.numberFormat,
    });
    setProjectFormatsVersion((version) => version + 1);
  }, [projectDetails?.projectDTO?.dateFormat, projectDetails?.projectDTO?.numberFormat]);
  // Try to derive an assessment path with IDs; fallback to simple /assessment
  const assessmentPath = (() => {
    // If already on assessment with ids, reuse them
    const path = location?.pathname || "";
    const match = path.match(/^\/assessment\/(.+?)\/(\d+)\/(\d+)/);
    if (match) {
      const [, pId, dId, vId] = match;
      return `/assessment/${pId}/${dId}/${vId}?tab=design`;
    }
    // Else, try from projectDetails first available design/version
    const designs = projectDetails?.designComponentsList;
    if (projectId && Array.isArray(designs) && designs.length > 0) {
      const firstDesign = designs[0];
      const dId = firstDesign?.designId;
      const firstVersion = Array.isArray(firstDesign?.designVersionComponents)
        ? firstDesign.designVersionComponents[0]
        : null;
      const vId = firstVersion?.designVersionId;
      if (dId && vId) {
        return `/assessment/${projectId}/${dId}/${vId}?tab=design`;
      }
    }
    // Final fallback
    return "/assessment?tab=design";
  })();

  const [status, setStatus] = useState({
    reportReadyStatus: false,
    navStatus: true,
    enableAssetPage: false,
    enableProgramPage: false,
    enableSystemPage: false,
    enableSecureArchitecturePage: false,
    enableBOMPage: false,
    enableDeployPage: false,
  });
  // Use Jotai atoms for access and user scope
  const [scopeListAccess] = useAtom(scopeListAccessAtom);
  const [userScope, setUserScope] = useAtom(userScopeAtom);
    const [tour] = useAtom(tourAtom);
    const { startTour } = useTour();

  const [maintenanceNotice, setMaintenanceNotice] = useState(null);
  const [maintenancePopoverOpened, setMaintenancePopoverOpened] = useState(false);

  // Fetch scheduled maintenance once on login/mount and show the notice the first time
  useEffect(() => {
    apiSession
      .getMaintenance()
      .then((response) => {
        const payload = response?.data ?? response;
        const data = Array.isArray(payload) ? payload[0] : payload;

        if (!data || data.enabled === false) return;

        // Show only if the maintenance starts today or later (matched by date, not exact time)
        const startDay = dayjs(data.startDateTime).startOf("day");
        const today = dayjs().startOf("day");
        if (data.startDateTime && startDay.isBefore(today)) return;

        setMaintenanceNotice(data);

        const noticeKey = `${data.startDateTime || ""}_${data.endDateTime || ""}`;
        const seenKey = localStorage.getItem("mga-maintenance-notice-seen");

        if (seenKey !== noticeKey) {
          setMaintenancePopoverOpened(true);
        }
      })
      .catch(() => {
        // Silently ignore - the maintenance notice is a non-critical enhancement
      });
  }, []);

  const dismissMaintenanceNotice = () => {
    setMaintenancePopoverOpened(false);
    if (maintenanceNotice) {
      const noticeKey = `${maintenanceNotice.startDateTime || ""}_${maintenanceNotice.endDateTime || ""}`;
      localStorage.setItem("mga-maintenance-notice-seen", noticeKey);
    }
  };

  // Sync atom with localStorage on mount and when returning from login
  useEffect(() => {
    const scopeItem = localStorage.getItem('scope');
    if (scopeItem) {
      try {
        const parsedScope = JSON.parse(scopeItem);
        setUserScope(parsedScope);
      } catch (e) {
        console.error('Failed to parse scope from localStorage:', e);
      }
    }
  }, [setUserScope]);

  useEffect(() => {
    if (!tour) return;
    let cancelled = false;
    const hasSteps = () => document.querySelector("[data-intro]");
    const attemptStart = (tries = 0) => {
      if (cancelled) return;
      if (hasSteps()) {
        startTour();
        return;
      }
      if (tries > 40) {
        // fallback ~40 frames (~600ms) then start anyway
        startTour();
        return;
      }
      requestAnimationFrame(() => attemptStart(tries + 1));
    };
    attemptStart();
    return () => {
      cancelled = true;
    };
  }, [tour, startTour]);
  useEffect(() => {
    projectNavigationRef.current.addEventListener("didClick", () => {
      document.getElementById("app2-sidemenu").toggle();
      navigate("/dashboard");
    });
  }, []);

  useEffect(() => {
    if (!legalNavigationRef.current) return;
    const onLegalClick = () => {
      setLegalTab("terms");
    };
    legalNavigationRef.current.addEventListener("didClick", onLegalClick);
    return () => {
      legalNavigationRef.current?.removeEventListener("didClick", onLegalClick);
    };
  }, []);

  const onButtonLogout = () => {
    localStorage.clear();
    apiSession
      .logout()
      .then((response) => {
        navigate("/");
      })
      .catch((error) => {
        navigate("/");
      });
    navigate("/");
  };
  // userScope is now provided by userScopeAtom

  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  useEffect(() => {
    localStorage.getItem == "dark"
      ? document.body.classList.add("se-theme-dark")
      : document.body.classList.remove("se-theme-dark");
  }, [colorScheme]);

  useEffect(() => {
    colorScheme === "dark"
      ? document
          .getElementsByTagName("se-app")?.[0]
          ?.classList.add("se-theme-dark")
      : document
          .getElementsByTagName("se-app")?.[0]
          ?.classList.remove("se-theme-dark");
  }, []);

  const dark = colorScheme === "dark";
  const [lang, setLang] = useState(i18n.language || 'en');
  useEffect(() => {
    const handleLanguageChanged = (nextLanguage) => {
      setLang(nextLanguage?.slice(0, 2)?.toLowerCase() || "en");
    };

    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);
  }, [i18n]);

  useEffect(() => {
    const stored = localStorage.getItem('i18nextLng')?.slice(0, 2)?.toLowerCase();
    const nextLang = env.REACT_APP_ENABLE_LNG === true && stored === 'fr' ? 'fr' : 'en';

    if (i18n.language !== nextLang) {
      i18n.changeLanguage(nextLang);
    }

    setLang(nextLang);

    if (stored !== nextLang) {
      localStorage.setItem('i18nextLng', nextLang);
    }
  }, []);

  useEffect(() => {
    if (env.REACT_APP_ENABLE_LNG !== true) return;

    let cancelled = false;
    apiSession
      .getUserProfile()
      .then((profile) => {

        const getLanguageCode = (lang) => lang?.slice(0, 2)?.toLowerCase();
 
        const preferredLanguage =
        getLanguageCode(localStorage.getItem('i18nextLng')) ||
        getLanguageCode(profile?.preferredLanguage);

        if (cancelled || !['en', 'fr'].includes(preferredLanguage)) return;

        if (i18n.language !== preferredLanguage) {
          i18n.changeLanguage(preferredLanguage);
        }

        localStorage.setItem('i18nextLng', preferredLanguage);
        setLang(preferredLanguage);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  const changeLanguage = (lng) => {
    const nextLang = env.REACT_APP_ENABLE_LNG === true && lng === 'fr' ? 'fr' : 'en';
    i18n.changeLanguage(nextLang);
    localStorage.setItem('i18nextLng', nextLang);
    setLang(nextLang);
  };
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [legalTab, setLegalTab] = useState(null);

  const openProfile = () => setProfileModalOpen(true);

  return (
    <>
      <se-header app-title={i18n.t('app.headerTitle')} style={{ zIndex: "auto" }}>
        <se-sidemenu id="app2-sidemenu" style={{ zoom: "118%" }}>
          <se-sidemenu-item
            item={i18n.t('menu.projects')}
            aria-label={i18n.t('menu.projects')}
            ref={projectNavigationRef}
          ></se-sidemenu-item>

          <se-sidemenu-item item={i18n.t('menu.about')} aria-label={i18n.t('menu.about')}>
            <se-about
              app-title={i18n.t('app.headerTitle')}
              version={env.REACT_APP_VERSION}
              image-url={src}
            ></se-about>
          </se-sidemenu-item>
          <se-sidemenu-item item={i18n.t('menu.legal')} aria-label={i18n.t('menu.legal')} ref={legalNavigationRef}>
            <div className="col-md-12">
              <div className=" mt-2 p-2 legal-border">
                <Tabs value={legalTab} onTabChange={setLegalTab} color="seGreen.0">
                  <Tabs.List>
                    <Tabs.Tab value="terms">Terms and Conditions</Tabs.Tab>
                    <Tabs.Tab value="privacy">Privacy Policy</Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="terms" pt="md">
                    
                    {legalTab === "terms" && (
                      <div style={{  overflowY: "auto" }}>
                        <LegalDocument pageWidth={700} />
                      </div>
                    )}
                  </Tabs.Panel>

                  <Tabs.Panel value="privacy" pt="md">
                    
                    <h4>{i18n.t('privacy.policyTitle')}</h4>
                    <a
                      href={privacyPolicy}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={i18n.t('menu.legal')}
                    >
                      <h5>{privacyPolicy}</h5>
                    </a>
                  </Tabs.Panel>
                </Tabs>
              </div>
            </div>
          </se-sidemenu-item>
        </se-sidemenu>

        <div slot="end">
          {window.location.href.includes("/assessment") &&
            activeState.toLowerCase() === "design" &&
            userScope?.access?.contextMenu && (
              <SeIcon
                title={i18n.t('action.help')}
                size="medium"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setCollapseSideMenu((prev) => ({
                    ...prev,
                    leftMenu: true,
                  }));
                  setSideMenu((prev) => ({
                    showMenu: !prev.showMenu,
                  }));
                }}
              >
                {sideMenuShow?.showMenu
                  ? "information_circle"
                  : "information_stroke"}
              </SeIcon>
            )}
          {maintenanceNotice && (
            <>
              &nbsp; &nbsp;
              <Popover
                opened={maintenancePopoverOpened}
                onChange={setMaintenancePopoverOpened}
                position="bottom-end"
                width={420}
                withArrow
                arrowSize={14}
                arrowOffset={22}
                shadow="none"
                zIndex={9999}
                transition="pop"
                transitionDuration={150}
                styles={{
                  dropdown: {
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    boxShadow: "none",
                    overflow: "visible",
                  },
                  arrow: {
                    background:
                      theme.colorScheme === "dark" ? "#202728" : "#FFFFFF",
                    border: `1px solid ${
                      theme.colorScheme === "dark" ? "#384248" : "#E4E7EB"
                    }`,
                  },
                }}
              >
                <Popover.Target>
                  <ActionIcon
                    variant="outline"
                    color="seGreen.0"
                    title="Scheduled Maintenance"
                    onClick={() => setMaintenancePopoverOpened((o) => !o)}
                  >
                    <IconCalendarDue size="1.1rem" />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown>
                  <Paper
                    radius={12}
                    p={24}
                    sx={{
                      width: "100%",
                      maxWidth: 440,
                      boxSizing: "border-box",
                      overflow: "visible",
                      border: `1px solid ${
                        theme.colorScheme === "dark" ? "#384248" : "#E4E7EB"
                      }`,
                      background:
                        theme.colorScheme === "dark" ? "#202728" : "#FFFFFF",
                      boxShadow: "0 12px 40px rgba(0,0,0,.25)",
                      "@media (max-width: 900px)": { width: "90vw" },
                      "@media (max-width: 480px)": { width: "95vw" },
                    }}
                  >
                    <Group spacing={16} align="center" mb={20} noWrap>
                      <ThemeIcon
                        radius="xl"
                        size={40}
                        sx={{
                          background:
                            theme.colorScheme === "dark"
                              ? "rgba(54, 201, 95, 0.15)"
                              : "rgba(54, 201, 95, 0.1)",
                          color: "#36C95F",
                        }}
                      >
                        <IconCalendarDue size={20} color="#36C95F" />
                      </ThemeIcon>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Text
                          sx={{
                            fontSize: 18,
                            fontWeight: 700,
                            lineHeight: 1.3,
                            color:
                              theme.colorScheme === "dark"
                                ? "#FFFFFF"
                                : "#222222",
                          }}
                        >
                          Scheduled Maintenance
                        </Text>
                        <Text
                          sx={{
                            fontSize: 14,
                            fontWeight: 400,
                            marginTop: 4,
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            color:
                              theme.colorScheme === "dark"
                                ? "#A6ADB4"
                                : "#6C737F",
                          }}
                        >
                          Application will undergo planned maintenance during
                          the window below.
                        </Text>
                      </Box>
                    </Group>

                    <Group position="apart" align="center" noWrap mb={20}>
                      <Stack spacing={6} sx={{ flex: 1, minWidth: 0 }}>
                        <Text
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                            color:
                              theme.colorScheme === "dark"
                                ? "#A6ADB4"
                                : "#6C737F",
                          }}
                        >
                          Start
                        </Text>
                        <Text
                          sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color:
                              theme.colorScheme === "dark"
                                ? "#FFFFFF"
                                : "#222222",
                          }}
                        >
                          {maintenanceNotice.startDateTime
                            ? dayjs(maintenanceNotice.startDateTime).format(
                                "DD MMM YYYY"
                              )
                            : "-"}
                        </Text>
                        <Text
                          sx={{
                            fontSize: 16,
                            fontWeight: 500,
                            color:
                              theme.colorScheme === "dark"
                                ? "#A6ADB4"
                                : "#6C737F",
                          }}
                        >
                          {maintenanceNotice.startDateTime
                            ? dayjs(maintenanceNotice.startDateTime).format(
                                "hh:mm A"
                              )
                            : "-"}
                        </Text>
                      </Stack>

                      <ActionIcon
                        variant="transparent"
                        size={20}
                        sx={{
                          color:
                            theme.colorScheme === "dark"
                              ? "#A6ADB4"
                              : "#6C737F",
                        }}
                      >
                        <IconArrowRight size={16} />
                      </ActionIcon>

                      <Stack
                        spacing={6}
                        sx={{ flex: 1, minWidth: 0, alignItems: "flex-end" }}
                      >
                        <Text
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                            color:
                              theme.colorScheme === "dark"
                                ? "#A6ADB4"
                                : "#6C737F",
                          }}
                        >
                          Estimated End
                        </Text>
                        <Text
                          sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color:
                              theme.colorScheme === "dark"
                                ? "#FFFFFF"
                                : "#222222",
                          }}
                        >
                          {maintenanceNotice.endDateTime
                            ? dayjs(maintenanceNotice.endDateTime).format(
                                "DD MMM YYYY"
                              )
                            : "-"}
                        </Text>
                        <Text
                          sx={{
                            fontSize: 16,
                            fontWeight: 500,
                            color:
                              theme.colorScheme === "dark"
                                ? "#A6ADB4"
                                : "#6C737F",
                          }}
                        >
                          {maintenanceNotice.endDateTime
                            ? dayjs(maintenanceNotice.endDateTime).format(
                                "hh:mm A"
                              )
                            : "-"}
                        </Text>
                      </Stack>
                    </Group>

                    {maintenanceNotice.description && (
                      <Group
                        spacing={12}
                        align="flex-start"
                        noWrap
                        p={16}
                        mb={20}
                        sx={{
                          borderRadius: 8,
                          background:
                            theme.colorScheme === "dark"
                              ? "#3A3E42"
                              : "#EEF5FF",
                        }}
                      >
                        <IconInfoCircle
                          size={18}
                          color="#2F80ED"
                          style={{ marginTop: 2, flexShrink: 0 }}
                        />
                        <Text
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 14,
                            fontWeight: 400,
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            color:
                              theme.colorScheme === "dark"
                                ? "#FFFFFF"
                                : "#222222",
                          }}
                        >
                          {maintenanceNotice.description}
                        </Text>
                      </Group>
                    )}

                    <Group position="right">
                      <Button
                        onClick={dismissMaintenanceNotice}
                        sx={{
                          height: 36,
                          borderRadius: 6,
                          paddingLeft: 16,
                          paddingRight: 16,
                          fontSize: 14,
                          fontWeight: 600,
                          backgroundColor: "#36C95F",
                          "&:hover": {
                            backgroundColor: "#2FB054",
                          },
                        }}
                      >
                        Okay
                      </Button>
                    </Group>
                  </Paper>
                </Popover.Dropdown>
              </Popover>
            </>
          )}
          &nbsp; &nbsp;
          <span
            data-intro={t("tour.toggleTheme")}
            data-position="bottom"
          >
            <ActionIcon
              variant="outline"
              color={dark ? "yellow" : "seGreen.0"}
              onClick={() => toggleColorScheme()}
              title={i18n.t('action.toggleTheme')}
            >
              {dark ? (
                <IconSunHigh size="1.1rem" />
              ) : (
                <IconMoonStars size="1.1rem" />
              )}
            </ActionIcon>
          </span>
          <Group position="center">
            {/* Profile menu */}
            <Group position="center" spacing="xs">
              <Menu withArrow>
                <Menu.Target>
                  <UnstyledButton style={{ marginLeft: "10px" }}>
                    <Group>
                      <div
                        style={{ display: "flex", alignItems: "center" }}
                        data-intro={t("tour.viewProfileOrLogout")}
                        data-position="bottom"
                      >
                        <Text
                          color={
                            theme.colorScheme === "dark" ? "white" : "#3dcd58"
                          }
                          size="sm"
                        >
                          <SeIcon aria-hidden="true">user_standard</SeIcon> &nbsp;
                        </Text>
                        <Text
                          size="sm"
                          weight={500}
                          sx={{
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={userScope?.name}
                        >
                          {userScope?.name}
                        </Text>
                      </div>

                      {<IconCaretDownFilled size="1rem" />}
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item component="button" onClick={openProfile}>
                    {i18n.t('profile.open')}
                  </Menu.Item>
                  <Menu.Item component="button" onClick={onButtonLogout}>
                    {i18n.t('profile.logout')}
                  </Menu.Item>
                  {/* <Menu.Divider />
                  <Menu.Label>{i18n.t('menu.language')}</Menu.Label>
                  <Menu.Item component="button" onClick={() => changeLanguage('en')}>
                    {i18n.t('language.english')}
                  </Menu.Item> */}
                  {/* <Menu.Item component="button" onClick={() => changeLanguage('fr')}>
                    {i18n.t('language.french')}
                  </Menu.Item> */}
                </Menu.Dropdown>
              </Menu>
              {/* Language dropdown (separate control) */}

             { <Menu withinPortal>
                <Menu.Target>
                  <UnstyledButton
                    style={{ marginLeft: "6px" }}
                    title={i18n.t("menu.language")}
                  >
                    <Group spacing={4}>
                      <SeIcon aria-hidden="true">web_sdk</SeIcon>

                      <Text size="sm" weight={500}>
                        {lang === "fr" ? "FR" : "EN"}
                      </Text>
                      <IconCaretDownFilled size="0.9rem" />
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    component="button"
                    onClick={() => changeLanguage("en")}
                  >
                    EN {lang === "en" ? "✓" : ""}
                  </Menu.Item>
                  {env.REACT_APP_ENABLE_LNG === true && <Menu.Item
                    component="button"
                    onClick={() => changeLanguage("fr")}
                  >
                    FR {lang === "fr" ? "✓" : ""}
                  </Menu.Item>}
                </Menu.Dropdown>
              </Menu>}
            </Group>
          </Group>
        </div>
      </se-header>

      <se-tabbar color="primary" aria-label="main-navigation-tabbar">
        <nav style={status.navStatus ? {} : { display: "none" }}>
          <NavLink
            key={"Projects"}
            to={"/dashboard"}
            activeClassName="active"
            className="ml-3"
            style={{
              color: colorScheme === "dark" ? "black" : "white",
            }}
          >
            <span className="ml-0">{i18n.t('menu.projects')}</span>
          </NavLink>
          <NavLink
            key={"Assets"}
            to={"/library"}
            activeClassName="active"
            className="ml-3"
            style={{
              color: colorScheme === "dark" ? "black" : "white",
              display: !window.location.pathname.includes?.("/assessment")
                ? ""
                : "none",
            }}
          >
            <span className="ml-0">{i18n.t('menu.assets')}</span>
          </NavLink>
          {scopeListAccess?.getUsers && (
            <NavLink
              key={"User Management"}
              to={"/users"}
              activeClassName="active"
              className="ml-3"
              style={{
                color: colorScheme === "dark" ? "black" : "white",
                display: !window.location.pathname.includes?.("/assessment")
                  ? ""
                  : "none",
              }}
            >
              <span className="ml-0">{i18n.t('menu.userManagement')}</span>
            </NavLink>
          )}
          {scopeListAccess?.getAllTickets && (
            <NavLink
              key={"Tickets"}
              to={"/tickets"}
              activeClassName="active"
              className="ml-3"
              style={{
                color: colorScheme === "dark" ? "black" : "white",
                display: !window.location.pathname.includes?.("/assessment")
                  ? ""
                  : "none",
              }}
            >
              <span className="ml-0">{i18n.t('menu.tickets')}</span>
            </NavLink>
          )}
          {userScope?.access?.ticketNotification && (
            <NavLink
              key={"Configurations"}
              to={mgaConstants?.routes?.configurations || "/configurations"}
              activeClassName="active"
              className="ml-3"
              style={{
                color: colorScheme === "dark" ? "black" : "white",
                display: !window.location.pathname.includes?.("/assessment")
                  ? ""
                  : "none",
              }}
            >
              <span className="ml-0">{i18n.t('menu.configurations')}</span>
            </NavLink>
          )}
          <NavLink
            key={"assesment"}
            to={assessmentPath}
            exact={false}
            style={{
              ...(colorScheme === "dark"
                ? { color: "black" }
                : { color: "white" }),
              pointerEvents: !window.location.href.includes?.("/assessment")
                ? "none"
                : "all",
              display: !window.location.href.includes?.("/assessment")
                ? "none"
                : "",
            }}
            disabled={
              window.location.href.includes?.("/assessment") ? false : true
            }
            activeClassName={
              window.location.href.includes?.("/assessment")
                ? "active"
                : "inactive"
            }
            className="ml-3"
          >
            <span className="ml-0">{projectName}</span>
          </NavLink>
        </nav>
        <nav
          slot="edge"
          overflow="compact"
          className=" no-events se-subHeader-14px se-green-background se-custom-nav-header "
        >
          <div className="truncate-projectName-header d-flex flex-column"></div>
        </nav>
      </se-tabbar>
      {props.children}
      <se-snackbar
        id="snackbar-shell"
        type={snackBar.snackbarType}
        icon="information_stroke"
        message={snackBar.snackbarMessage}
        duration="3000"
      ></se-snackbar>
      {dialogSimulation.status && <SimulationDialog />}
      <HelpSection />
      <ProfileModal opened={profileModalOpen} onClose={() => setProfileModalOpen(false)} />
    </>
  );
};

export default MGAShellLayout;
